import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Copy, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { apiClient } from "../lib/api";

export default function SaveProgressModal({ open, onOpenChange, getState, currentStep }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await apiClient.post("/wizard/session", {
        email,
        customer_name: name || null,
        state: getState(),
        current_step: currentStep,
      });
      setResult(data);
      if (data.email_sent) {
        toast.success("Sent! Check your email for the resume link.");
      } else {
        toast.success("Progress saved! Copy the resume link below.");
      }
    } catch (err) {
      toast.error("Could not save progress. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!result?.resume_url) return;
    try {
      await navigator.clipboard.writeText(result.resume_url);
      toast.success("Resume link copied");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const close = () => {
    onOpenChange(false);
    // reset after dialog closes
    setTimeout(() => {
      setResult(null);
      setEmail("");
      setName("");
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="save-progress-modal">
        {!result ? (
          <>
            <DialogHeader>
              <div className="w-12 h-12 rounded-2xl bg-sky-100 flex items-center justify-center mb-3">
                <EnvelopeSimple size={24} weight="duotone" className="text-sky-600" />
              </div>
              <DialogTitle className="font-display text-2xl font-bold text-slate-950">
                Save your progress
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                We'll email you a link to pick up exactly where you left off — no need to
                re-upload your bill.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-4">
              <label className="block">
                <span className="block text-sm font-semibold text-slate-700 mb-2">
                  Your email *
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-clean"
                  placeholder="you@example.com"
                  data-testid="save-email-input"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-semibold text-slate-700 mb-2">
                  First name (optional)
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-clean"
                  data-testid="save-name-input"
                />
              </label>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={close}
                  className="btn-secondary flex-1 justify-center inline-flex"
                  data-testid="save-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="btn-primary flex-1 justify-center inline-flex disabled:opacity-50"
                  data-testid="save-submit-btn"
                >
                  {loading ? "Saving..." : "Send link"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle size={24} weight="fill" className="text-emerald-600" />
              </div>
              <DialogTitle className="font-display text-2xl font-bold text-slate-950">
                {result.email_sent ? "Email sent!" : "Progress saved"}
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                {result.email_sent
                  ? `We sent a resume link to ${email}. Check your inbox.`
                  : "Copy this link to resume your analysis on any device."}
              </DialogDescription>
            </DialogHeader>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mt-4 break-all text-sm font-mono text-slate-700">
              {result.resume_url}
            </div>

            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={copyLink}
                className="btn-secondary flex-1 justify-center inline-flex gap-2"
                data-testid="save-copy-btn"
              >
                <Copy size={18} /> Copy link
              </button>
              <button
                type="button"
                onClick={close}
                className="btn-primary flex-1 justify-center inline-flex"
                data-testid="save-done-btn"
              >
                Got it
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
